import { ContentStudioService } from './../../services/content-studio.service';
import { Component, OnInit, OnDestroy, Input, Output, HostListener, Optional, Inject, EventEmitter } from '@angular/core';
import { LogService, PershingTranslationService } from '@pershing/pershing-core';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { textEditorConfig, textTitleEditorConfig } from './../../static/TextEditorConfig';
import { CMSEvents } from '../../static/ContentManagementConstants';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ProgressDialogComponent } from '../progress-dialog/progress-dialog.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'cms-text',
  templateUrl: 'cms-text.component.html'
})

export class CMSTextComponent implements OnInit, OnDestroy {
  @Input() siteId: string; // specify the site id in Crafter
  @Input() stagingSiteId: string; // specify the site id in Crafter
  // contentPath initial value set to make sure the root of hierarchy is retail/generic
  @Input() contentPath: string = 'retail/generic'; // pass to backend to know where to save the content and read the content
  @Input() stagingContentPath: string = ''; // pass to backend to know where to save the content
  @Input() contentName: string; // aka file name, internal name, article id. ex: ABOUT_US
  @Input() titleContentName: string; // the name how we call this content. ex: About Us
  @Input() contentHierarchy: string[] = [];
  @Input() contentVersion: string = ''; // for future support versioning
  @Input() componentId: string = ''; // passed from siteTemplate, used for page edit
  @Input() userId: string = 'dummyUser';
  @Input() isStaging: boolean;
  @Input() isEditOnlyMode: boolean = false; // For Overwriting toggle mode and keep the component always in edit mode.
  @Output() onPreviewContent: EventEmitter<any> = new EventEmitter<any>(); // Consuming component could emit event to delete the content.
  @Input() deleteContentEventEmitter: EventEmitter<any>;
  @Input() isComingFromCreatedSite: boolean = true; // to differentiate the component being used in SB or actual site
  @Input() cmsLocale: string = 'en';
  @Input() showContentHeader: boolean;
  config: AngularEditorConfig = textEditorConfig;
  titleConfig: AngularEditorConfig = textTitleEditorConfig;
  persistedText: string = '';
  persistedTitle: string = '';
  htmlContent: string = '';
  titleHtmlContent: string = '';
  public isEditMode: boolean = false;
  titlePlaceHolderText: string = '';
  bodyPlaceHolderText: string = '';
  prefixTitlePlaceHolderText: string = 'Enter Title Here for ';
  prefixBodyPlaceHolderText: string = 'Enter Text Here for ';
  isFromSiteBuilder: boolean;
  disableSave: boolean = true;
  isCmsEdited: boolean = false;
  isSitebuilderCMSEnabled: boolean = false;
  cdnHost: string = '';
  language: string = '';

  unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private logService: LogService,
    private contentservice: ContentStudioService,
    @Optional() @Inject('AppSettings') private appSettings: any,
    private translationService: PershingTranslationService,
    public dialog: MatDialog,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.siteId = this.siteId || this.appSettings.cms.siteId;
    this.stagingSiteId = this.stagingSiteId || this.appSettings.cms.stagingSiteId;
    this.isStaging = this.isStaging || this.appSettings.ibdConfigMap.staging;
    this.translationService.addTranslationFolder('@pershing/nxi-contentmanagement', 'node_modules/@pershing/nxi-contentmanagement/lib');
    this.isFromSiteBuilder = !this.isComingFromCreatedSite;
    this.isEditMode = this.isEditOnlyMode ? true : false;
    this.language = this.translate.currentLang;
    this.cdnHost = this.appSettings.siteCdnUrl;
    this.isCmsEdited = false;
    this.isSitebuilderCMSEnabled = this.appSettings.isSitebuilderCMSEnabled;
    if (this.isSitebuilderCMSEnabled) {
      this.resolvePaths();
      this.getContent();  
    } else {
      this.fetchContentFromAkamai();
    }
    if (!!this.titleContentName) {
      // this.titlePlaceHolderText = this.translationService.translate.instant('CMS.TEXT_TITLE_PROMPT') + this.titleContentName;
      // this.bodyPlaceHolderText = this.translationService.translate.instant('CMS.TEXT_BODY_PROMPT')  + this.titleContentName;
      // TODO:  Above not translated.  Use below for now.
      this.titlePlaceHolderText = this.prefixTitlePlaceHolderText + this.titleContentName;
      this.bodyPlaceHolderText = this.prefixBodyPlaceHolderText + this.titleContentName;
    }
    if (this.deleteContentEventEmitter) {
      this.deleteContentEventEmitter.pipe(takeUntil(this.unsubscribe)).subscribe((msg) => {
        if (msg && msg.contentName === this.contentName) {
          // TODO:  Rename this to 'updateContent' and check for specific action.
          // this.deleteContent();
          this.htmlContent = '<div style="display:none" id="hidden-cms-text"></div>';
          this.titleHtmlContent = '<div style="display:none" id="hidden-cms-title"></div>';
          this.writeContent();
        }
      });
    }
  }

  /**
   * To resolve contentPath and stagingContentPath.
   * Will try to obtain the URL either from Input or appSettings then take that URL turn into path in Crafter
   * ex: somesite.netxinvestor.com => will get us contentPath === retail/somesite-netxinvestor-com/conservative/en
   */
  resolvePaths() {
    // This component is used by both SB and NXI.  Further SB use this for both edit and non edit mode (preview.)
    // @Input generally for initial value while appSettings is for specific app.
    // contentPath should not be generic or default.  Add it to hierarchy instead to ensure generic is not being considered first.
    if (!!this.contentPath && this.contentPath.indexOf('generic') > 0) {
      this.contentHierarchy.push(this.contentPath);
      this.contentPath = '';
    }
    // when edit the content from SB, we should pass it through input on where to save the content
    // if it's there, don't try to resolve contentpath again
    if (!this.contentPath && !this.stagingContentPath
      && this.appSettings && this.appSettings.ibdConfigMap && !!this.appSettings.ibdConfigMap.URL) {
      // resolve theme type
      let themeType = 'conservative';
      let siteUrl = '';
      let stagingSiteUrl = '';
      if (this.appSettings.isSitebuilder) {
        // Check if it (demo) site that is used for preview an ibd else SB site community has staging or publish prefix so remove that.
        siteUrl = !!this.appSettings.previewSite ? this.appSettings.previewSite : this.appSettings.ibdConfigMap.URL;
        siteUrl = this.massageUrl(siteUrl) // removing staging prefix and replace . with -
        stagingSiteUrl = this.resolveStagingSiteUrl();
        stagingSiteUrl = this.massageUrl(stagingSiteUrl);
        // path in Crafter consists of platform + site URL (sub domain & domain) + theme.
        this.contentPath = this.appSettings.platform + '/' + siteUrl + '/' + themeType;
        this.stagingContentPath = this.appSettings.platform + '/' + stagingSiteUrl + '/' + themeType;
      } else {
        // this else block is for something in the future
        this.contentPath = this.appSettings.ibdConfigMap.URL.replace(/\./g, '-');
      }
    }
    if (this.isComingFromCreatedSite) {
      this.contentPath += `/${this.translationService.userLang}`;
      this.stagingContentPath += `/${this.translationService.userLang}`;
    } else {
      this.contentPath += `/${this.cmsLocale}`;
      this.stagingContentPath += `/${this.cmsLocale}`;
    }
  }

  /**
   * Get url of the site from appSettings then based on region return the staging url
   * ex: somesite.qa.netxinvestor.com => somesite.test.netxinvestor.com
   * @returns the staging url
   */
  private resolveStagingSiteUrl(): string {
    let url = this.appSettings.ibdConfigMap.URL;
    if (!this.isStaging) {
      const currentRegion = url.split('.')[1]; // dev, test, qa, netxinvestor
      const domain = url.substr(url.indexOf('.', url.indexOf('.') + 1) + 1);
      if (currentRegion === 'dev' || currentRegion === 'test') {
        return this.appSettings.ibdConfigMap.community + '.dev.' + domain;
      } else if (currentRegion === 'qa') {
        return this.appSettings.ibdConfigMap.community + '.test.' + domain;
      } else {
        return this.appSettings.ibdConfigMap.community + '.qa.' + url.substr(url.indexOf('.') + 1);
      }
    } else {
      return url;
    }
  }

  /**
   * For usecase when in Iframe, will receive message from parent
   * @param event event
   */
  @HostListener('window:message', ['$event'])
  receiveMessage(event: any) {
    if (event && event.data && event.data.action && event.data.action === 'previewContent'
      && event.data.contentName && event.data.contentName === this.contentName) {
      this.titleHtmlContent = event.data && event.data.titleText ? this.encloseWithH2(event.data.titleText) : '';
      this.htmlContent = event.data && event.data.bodyText ? event.data.bodyText : '';
    }
  }

  @HostListener('document:' + CMSEvents.EDIT_COMPONENT, ['$event'])
  editComponentEventListener(event: any) {
    // Check to see if the componentId in the payload coorelates to our contentName
    if (event && event.detail && event.detail.data && event.detail.data.componentId === this.componentId) {
      this.toggleEditMode(true);
    }
  }

  toggleEditMode(isOn: boolean): void {
    this.isEditMode = this.isEditOnlyMode ? true : isOn;
  }

  getContent(): void {
    // always fetch staging site content when coming from SB
    let pathToGetContent = this.isFromSiteBuilder ? this.stagingContentPath : this.contentPath;
    this.contentservice.getContent(this.siteId, this.stagingSiteId, this.contentName, pathToGetContent, this.contentHierarchy, this.isStaging, this.isComingFromCreatedSite).pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      this.processReponse(res);
      if (this.isEditOnlyMode) {
        // For editing, need the editor show either (2) specific site content or (2) default content, then emit the content for preview.
        // For the case when user previouly selected to completely hide the content, we need to pull generic text.
        let hasHidenTitle: boolean = !!this.titleHtmlContent && this.titleHtmlContent.indexOf('hidden-cms-title') > 0;
        let hasHidenText: boolean = !!this.htmlContent && this.htmlContent.indexOf('hidden-cms-text') > 0;
        if (hasHidenTitle && hasHidenText) {
          // Previously chosen not to be displayed but now want to display so get generic text.
          this.getGenericContent();
        } else {
          this.previewContent('initContent');
        }
      }
    });
  }

  fetchContentFromAkamai(): void {
    let lang = this.language;
    let url = this.cdnHost;
    if (this.language == "es") {
        lang = "es";
    }
    let func = function (contentName, thisObj) {
      let abc: string = contentName;
      window[abc] = function (data) {
          if (data !== undefined && data !== null) {
            thisObj.processResponseFromAkamai(data);
          }
      }
    }(this.contentName, this);
    let scriptElement = document.createElement("script");
    // Set the src and id attributes of the script element
    scriptElement.src = url + '/webcontent/' + lang + '/' + this.contentName +'.js';
    scriptElement.type = 'text/javascript';
    
    // Get the head element
    let node = document.getElementsByTagName("head")[0];
    node.appendChild(scriptElement);
  }

  processResponseFromAkamai(data): void {
    if (data) {
      this.htmlContent = data.substring(data.indexOf("<body>") + 6, data.indexOf("</body>"));
      this.titleHtmlContent = data.substring(data.indexOf("<title>") + 7, data.indexOf("</title>"));
      let titleContent = this.titleHtmlContent.replace(/(<([^>]+)>)/gi, '');
      let contentLink = document.getElementsByName('cms-text-link-' + this.contentName);
      if (contentLink && contentLink.length > 0) {
        contentLink[0].innerHTML = titleContent;
      }
    }
    this.persistedTitle = this.titleHtmlContent;
    this.persistedText = this.htmlContent;
    
    this.previewContent('initContent');
  }

  getGenericContent() {
    this.contentservice.getContent(this.siteId, this.stagingSiteId, this.contentName, 'retail/generic', this.contentHierarchy, this.isStaging, this.isComingFromCreatedSite).pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      this.processReponse(res);
      this.previewContent('initContent');
      this.disableSave = false;
    });
  }

  processReponse(res: any) {
    this.logService.info(res);
    let stringifiedData: any = JSON.stringify(res);
    let parsedJson: any = JSON.parse(stringifiedData);
    let content: any = parsedJson['response'];
    if (content) {
      stringifiedData = JSON.stringify(content);
      parsedJson = JSON.parse(stringifiedData);
      this.htmlContent = parsedJson['content'] || '';
      this.titleHtmlContent = parsedJson['title'] || '';
      let titleContent = this.titleHtmlContent.replace(/(<([^>]+)>)/gi, '');
      let contentLink = document.getElementsByName('cms-text-link-' + this.contentName);
      if (contentLink && contentLink.length > 0) {
        contentLink[0].innerHTML = titleContent;
      }
    }
    this.persistedTitle = this.titleHtmlContent;
    this.persistedText = this.htmlContent;
  }

  writeContent(): void {
    this.persistedText = this.htmlContent;
    this.persistedTitle = this.encloseWithH2(this.titleHtmlContent);
    this.toggleEditMode(false);
    let dialogRef = this.dialog.open(ProgressDialogComponent, {
      width: '350px',
      height: '200px',
      panelClass: ['modal']
    });
    this.contentservice.writeContent(this.siteId, this.stagingSiteId, this.contentName, this.contentPath, this.stagingContentPath, this.persistedTitle, this.persistedText, this.userId).pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      this.logService.info(res);
      let stringifiedData: any = JSON.stringify(res);
      let parsedJson: any = JSON.parse(stringifiedData);

      let data = {
        action: CMSEvents.EDIT_CONTENT_VERSION_UPDATE,
        contentName: this.contentName,
        contentVersion: parsedJson['response']['contentVersion']
      };
      window.parent.postMessage(JSON.parse(JSON.stringify(data)), '*');
      if (this.isEditOnlyMode) {
        this.previewContent('saveContent');
      }
      dialogRef.close();
      this.disableSave = true;
    });
  }

  deleteContent(): void {
    this.contentservice.deleteContent(this.siteId, this.contentName, this.contentPath, false).pipe(takeUntil(this.unsubscribe)).subscribe((res) => {
      this.logService.info(res);
      this.htmlContent = '';
      this.titleHtmlContent = '';
      this.toggleEditMode(false);
    });
  }

  cancelContent(): void {
    this.htmlContent = this.persistedText;
    this.titleHtmlContent = this.persistedTitle;
    this.disableSave = true;
    this.toggleEditMode(false);
    if (this.isEditOnlyMode) {
      this.previewContent('cancelContent');
    }
  }

  /**
   * For SB create site flow where user would like to see the preview of CMS
   * in the context of NXI site which rendered within Pershing iframe to update itself.
   * This is to emit content to parent, so parent will then pass message into ifrmae
   * @param previewOnAction === previewContent
   */
  previewContent(previewOnAction: string) {
    this.onPreviewContent.emit({
      contentName: this.contentName,
      titleText: this.titleHtmlContent,
      bodyText: this.htmlContent,
      action: 'previewContent',
      previewOn: previewOnAction
    });
  }

  /**
   * To ensure the content's title is enclosed by h2 element
   * @param contentTitle content's title in html text
   * @returns contentTitle enclosed by h2 element
   */
  encloseWithH2(contentTitle: string): string {
    if (!!contentTitle && (contentTitle.indexOf('hidden-cms-title') > 0 || contentTitle.indexOf('<h2>') === 0)) {
      return contentTitle;
    } else if (!!contentTitle) {
      return '<h2>' + contentTitle + '</h2>'; // add h2 only if contentTitle is not empty
    } else {
      return ''; // Do not return empty heading tag as it is accessibility violation
    }
  }

  /**
   * 
   * @param siteUrl Remove staging- prefix and replace . with -
   */
  massageUrl(siteUrl: string) {
    let path = '';
    if (!!siteUrl) {
      // Remove prefix 'staging-' and convert all dot to dash.
      let domain = siteUrl.indexOf('?') > 0 ? siteUrl.slice(0, siteUrl.indexOf('?')) : siteUrl;
      path = domain.replace('staging-', '').replace(/\./g, '-');
    }
    return path;
  }

  onChange() {
    this.disableSave = this.persistedTitle === this.titleHtmlContent && this.persistedText === this.htmlContent;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
